/**
 * Grundrollen und ihre Startwerte (Story 28.2, Change „Rollen und Rechte“ §4.2 mit Rudolfs Antworten F1–F6 vom
 * 02.11.2026). Die Grundrollen sind Vorlagen; 28.3 legt sie als Daten an (`platform.role`, `platform.role_version`).
 *
 * Reihenfolge der Freischaltung (§5.8): Rechte werden zuerst nur enger, erst wenn der Schutz steht, weiter. Die
 * Erweiterung der Rolle Mitarbeiter (Aufträge, Kunden, Sendungsliste, Verkaufs- und Einkaufspreise; F5 = b) schaltet erst
 * Story 28.7 frei – bis dahin liefert `builtinRoleGrants('mitarbeiter', { stage: 'beforeRelease' })` den heutigen Stand.
 */
import type { Role } from '../actor.ts';
import { PERMISSION_AREAS, PERMISSION_CATALOG_VERSION, SPECIAL_PERMISSIONS } from './catalog.ts';
import { deepFreeze } from './deep-freeze.ts';
import { resolvePermissions, type EffectivePermissions, type RoleGrants, type RoleVersionInput } from './resolve.ts';

export const BUILTIN_ROLE_KEYS = ['admin', 'office', 'accounting', 'packplatz', 'mitarbeiter'] as const;
export type BuiltinRoleKey = (typeof BUILTIN_ROLE_KEYS)[number];

export interface BuiltinRoleEntry {
  key: BuiltinRoleKey;
  name: string;
  description: string;
  /** Nur die Admin-Grundrolle: alle Rechte, fest, nicht änderbar. */
  isAdmin: boolean;
}

/** Deutsche Namen und Beschreibungen (Entwurf 28.1). */
export const BUILTIN_ROLES: readonly BuiltinRoleEntry[] = deepFreeze([
  { key: 'admin', name: 'Admin', description: 'Darf alles. Fest, nicht änderbar – es bleibt immer mindestens ein Admin.', isAdmin: true },
  { key: 'office', name: 'Büro', description: 'Verkauf, Kunden, Artikel, Einkauf und Belege. Heute „Benutzer“.', isAdmin: false },
  { key: 'accounting', name: 'Buchhaltung', description: 'Zahlungseingänge, Offene Posten, Mahnungen und DATEV. Übriges nur ansehen.', isAdmin: false },
  { key: 'packplatz', name: 'Packplatz', description: 'Versand, Labels und Etiketten. Keine Preise, keine Kunden.', isAdmin: false },
  { key: 'mitarbeiter', name: 'Mitarbeiter', description: 'Aufgaben und Zeiterfassung. Kunden und Aufträge mit Preisen nur ansehen.', isAdmin: false },
]);

/** Abbildung der heutigen Rolle (`app_user.role`) auf die Grundrolle (Startbelegung 28.3, Change §5.2). */
export const LEGACY_ROLE_TEMPLATE: Readonly<Record<Role, BuiltinRoleKey>> = deepFreeze({
  user: 'office',
  admin: 'admin',
  packplatz: 'packplatz',
  mitarbeiter: 'mitarbeiter',
});

/** Story, die die Mitarbeiter-Erweiterung freischaltet (Change §5.8). */
export const MITARBEITER_RELEASED_IN = '28.7';

/** Rechte, um die die Rolle Mitarbeiter erst ab 28.7 erweitert wird ([RUDOLF] F5 = b; Sendungsliste #1725). */
export const MITARBEITER_STAGED_GRANTS: RoleGrants = deepFreeze({
  areas: { orders: 'read', customers: 'read', shipping: 'read' },
  specials: { 'prices:sales': true, 'prices:purchase': true },
});

const ADMIN_GRANTS: RoleGrants = deepFreeze({
  areas: Object.fromEntries(PERMISSION_AREAS.map((a) => [a.key, 'write'])),
  specials: Object.fromEntries(SPECIAL_PERMISSIONS.map((s) => [s.key, true])),
});

/** Startwerte nach §4.2 (Mitarbeiter: Zielstand ab 28.7). */
const GRANTS: Readonly<Record<BuiltinRoleKey, RoleGrants>> = deepFreeze({
  admin: ADMIN_GRANTS,
  office: {
    areas: {
      start: 'write',
      orders: 'write',
      customers: 'write',
      returns: 'write',
      articles: 'write',
      purchasing: 'write',
      shipping: 'write',
      labels: 'write',
      documents: 'write',
      // Zahlungseingänge: kein Zugriff [RUDOLF 02.11.2026]
      receivables: 'write',
      datev: 'write',
      reports: 'write',
      tasks: 'write',
      chat: 'write',
      ai: 'write',
    },
    specials: {
      'prices:sales': true,
      'prices:purchase': true,
      'data:export': true,
      'data:delete': true,
      'outbound:send': true,
      'data:bulk': true,
      'documents:issue': true,
      'ai:tasks': true,
      'mcp:ownKey': true,
      'time:leave': true,
      'chat:team': true,
      // Story 31.2 (Migration 0591): Agenten ansehen und anlegen [RUDOLF 04.11.2026].
      'agents:read': true,
      'agents:create': true,
    },
  },
  // Buchhaltung: keine Agenten-Rechte [RUDOLF 04.11.2026]; sie bekommt sie bei Bedarf über eine eigene Rolle (Epic 28).
  accounting: {
    areas: {
      start: 'write',
      orders: 'read',
      customers: 'read',
      returns: 'write',
      articles: 'read',
      purchasing: 'read',
      shipping: 'read',
      labels: 'write',
      documents: 'write',
      payments: 'write',
      receivables: 'write',
      datev: 'write',
      reports: 'write',
      tasks: 'write',
      chat: 'write',
      ai: 'write',
    },
    specials: {
      'prices:sales': true,
      'prices:purchase': true,
      'data:export': true,
      'data:delete': true,
      'outbound:send': true,
      'data:bulk': true,
      'documents:issue': true,
      'payments:book': true,
      'ai:tasks': true,
      'mcp:ownKey': true,
      'time:leave': true,
      'chat:team': true,
    },
  },
  // Story 24.1 (Migration 0564, Fassung 2): Sonderrecht Team-Chat für alle Rollen (AD-40) [HS55 03.11.2026].
  // Story 31.2 (Migration 0591, Fassung 3): Agenten-Eingang [RUDOLF 04.11.2026].
  packplatz: {
    areas: { shipping: 'write', labels: 'write' },
    specials: { 'outbound:send': 'dhlLabel', 'documents:issue': 'deliveryNote', 'chat:team': true, 'agents:inbox': true },
  },
  // Story 31.2 (Migration 0591): Agenten-Eingang in beiden Stufen (nicht Teil von MITARBEITER_STAGED_GRANTS).
  mitarbeiter: {
    areas: { tasks: 'write', ...MITARBEITER_STAGED_GRANTS.areas },
    specials: {
      'data:export': 'tasks',
      'data:delete': 'ownTasks',
      'data:bulk': 'tasks',
      'time:leave': true,
      'chat:team': true,
      'agents:inbox': true,
      ...MITARBEITER_STAGED_GRANTS.specials,
    },
  },
});

/** Stand der Freischaltung: `released` = Zielstand (§4.2), `beforeRelease` = vor 28.7 (§5.8). */
export type BuiltinRoleStage = 'beforeRelease' | 'released';

/**
 * Startwerte einer Grundrolle als frische Kopie (ändern ändert den Katalog nicht). `stage` ist Pflicht, damit nie still
 * der erweiterte Zielstand gilt (§5.8); ein unbekannter Wert wirft.
 */
export function builtinRoleGrants(key: BuiltinRoleKey, options: { stage: BuiltinRoleStage }): RoleGrants {
  if (options.stage !== 'beforeRelease' && options.stage !== 'released') throw new Error(`Unbekannte Stufe der Freischaltung: ${String(options.stage)}`);
  if (!Object.hasOwn(GRANTS, key)) throw new Error(`Unbekannte Grundrolle: ${String(key)}`);
  const grants = GRANTS[key];
  const areas = { ...grants.areas };
  const specials = { ...grants.specials };
  if (key === 'mitarbeiter' && options.stage === 'beforeRelease') {
    for (const area of Object.keys(MITARBEITER_STAGED_GRANTS.areas) as (keyof RoleGrants['areas'])[]) delete areas[area];
    for (const special of Object.keys(MITARBEITER_STAGED_GRANTS.specials) as (keyof RoleGrants['specials'])[]) delete specials[special];
  }
  return { areas, specials };
}

/** Grundrolle als Eingabe für `resolvePermissions`. */
export function builtinRoleVersion(key: BuiltinRoleKey, options: { stage: BuiltinRoleStage }): RoleVersionInput {
  return { isAdmin: key === 'admin', catalogVersion: PERMISSION_CATALOG_VERSION, grants: builtinRoleGrants(key, options) };
}

const LEGACY_EFFECTIVE = new Map<string, EffectivePermissions>();

/**
 * Wirksame Rechte der Grundrolle einer heutigen Rolle (`user`, `admin`, `packplatz`, `mitarbeiter`) im Stand vor 28.7;
 * jede andere Angabe (`null`, Rolle `ai_agent`) → `null`.
 *
 * Nur für Command-Kontexte ohne aufgelöste Rechte der Sitzung (Worker, geplante Pakete, Tests) – sie tragen bis 28.12
 * nur die Rolle. Die Startwerte im Code und die Fassung 2 der Grundrollen in der Datenbank sind gleich (Test
 * `roles-migration`); ab 28.9 (Rollen bearbeiten) müssen diese Wege die Rechte der Fassung mitgeben.
 */
export function legacyRolePermissions(role: string | null | undefined): EffectivePermissions | null {
  if (typeof role !== 'string' || !Object.hasOwn(LEGACY_ROLE_TEMPLATE, role)) return null;
  let e = LEGACY_EFFECTIVE.get(role);
  if (!e) {
    e = resolvePermissions(builtinRoleVersion(LEGACY_ROLE_TEMPLATE[role as Role], { stage: 'beforeRelease' }));
    LEGACY_EFFECTIVE.set(role, e);
  }
  return e;
}
